import { Button, Empty, Spin, Switch } from "antd";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { BiMessageSquareEdit } from "react-icons/bi";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { v4 as uuidV4 } from "uuid";
import { FaPlus } from "react-icons/fa6";
import { useScreenshot } from "use-react-screenshot";
import PropTypes from "prop-types";

import { endpoint } from "@/api";
import appConfig from "@/config/appConfig";
import { userRole } from "@/constant";
import { useSearchQuery } from "@/utils/useSearchQuery";
import CustomReportTemplateAddItemModal from "./CustomReportTemplateAddItemModal";
import CustomReportTemplateComponentModal from "./CustomReportTemplateComponentModal";
import CustomReportTemplateLayout from "./CustomReportTemplateLayout";
import MetaCustomReportTemplateModal from "./MetaCustomReportTemplateModal";

const CustomReportTemplateBody = ({
  mode,
  id,
  layouts,
  setLayouts,
  gridData = [],
  setGridData,
  selectedTiles = [],
  handleSelect = () => {},
}) => {
  const { userDetails } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const cp = useSearchQuery("cp") || undefined;
  const ref = useRef(null);

  // eslint-disable-next-line no-unused-vars
  const [image, takeScreenshot] = useScreenshot({
    type: "image/png",
    quality: 1.0,
  });

  // state
  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  // modal
  const [updateTemplateModal, setUpdateTemplateModal] = useState(false);
  const [componentModal, setComponentModal] = useState(false);
  const [addItemModal, setAddItemModal] = useState(false);
  const [editItem, setEditItem] = useState({});

  const { name } = data;

  const getTemplate = async () => {
    if (!isLoading) {
      setIsLoading(true);
      const res = await axios.get(endpoint.customReportTemplate + "/" + id, { withCredentials: true });
      // console.log({ res });
      if (res.status === 200) {
        if (cp === "true") {
          setData({ ...res.data?.result, name: res.data?.result?.name + "(copy)" });
        } else {
          setData(res.data.result);
        }
        if (res.data.result?.hasTabs) {
          const findActiveTab = res.data.result?.tabs?.find((tab) => tab.isSelected) || {};
          setLayouts(findActiveTab?.layouts || {});
          setGridData(findActiveTab?.items || []);
        } else {
          // console.log({ lay: res.data.result?.layouts });
          setLayouts(res.data.result?.layouts || {});
          setGridData(res.data.result?.items || []);
        }
        setIsLoading(false);
      }
    }
  };

  const handleUpdate = async (body) => {
    const response = await axios.put(endpoint.update_customReport_template + "/" + data._id, body, {
      withCredentials: true,
    });
    // console.log({ response });
    if (response.status === 204) {
      Swal.fire("", "Report Template Updated Successfully!", "success");
    }
  };

  const handleCreate = async (body) => {
    const response = await axios.post(endpoint.create_customReport_template, body, { withCredentials: true });
    // console.log({ response });
    if (response.status === 204) {
      Swal.fire("", "Report Template Updated Successfully!", "success");
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    const image = await takeScreenshot(ref.current);
    // eslint-disable-next-line no-unused-vars
    const { _id, createdAt, updatedAt, __v, ...rest } = data;
    const body = { ...rest, name, layouts, items: gridData, image };
    try {
      if (cp === "true") {
        body.reportType = uuidV4();
        await handleCreate(body);
        Swal.fire("Duplicated!", "Report Template Duplicated Successfully.", "success");
      } else {
        await handleUpdate(body);
        Swal.fire("Updated!", "Report Template has been Updated.", "success");
      }
      navigate("/report-template-list");
    } catch {
      Swal.fire("", "Error Happened", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (mode === "edit") {
      setIsEditable(true);
    }
  }, [mode]);

  useEffect(() => {
    getTemplate();
  }, [id]);

  useEffect(() => {
    document.title = `${appConfig.name} - Report Template`;
  }, []);

  return (
    <Spin spinning={isLoading}>
      <div>
        <div className="d-md-flex align-items-center justify-content-between">
          {mode !== "home" && (
            <div>
              <label className="az-content-label">Manage Report Template</label>
              <span className="d-block py-2">
                {mode === "view" ? "View" : "Edit"} Report Template: {data?.name}
              </span>
            </div>
          )}

          {/* edit options */}
          {![userRole.public, userRole.user].includes(userDetails?.role) && mode !== "view" && (
            <div className="d-flex flex-column flex-md-row gap-2 align-items-md-center align-items-start ms-auto mb-3">
              {isEditable && (
                <>
                  <Button
                    className="bg-secondary-subtle text-primary fw-medium"
                    type="primary"
                    icon={<BiMessageSquareEdit />}
                    onClick={() => setUpdateTemplateModal((pre) => !pre)}
                  >
                    Edit Report Template
                  </Button>
                  <Button
                    type="primary"
                    className="fw-medium"
                    onClick={() => setComponentModal((pre) => !pre)}
                    icon={<FaPlus />}
                  >
                    Add Items
                  </Button>
                </>
              )}
              {mode === "home" && (
                <Switch
                  checked={isEditable}
                  onChange={(e) => {
                    setIsEditable(e);
                    if (!e) {
                      handleUpdate();
                    }
                  }}
                />
              )}
            </div>
          )}
        </div>
        {mode !== "home" && <hr className="mt-0 mb-4" />}

        {/* body */}

        {gridData?.length > 0 ? (
          <div className="pb-5 px-3">
            <div className="bg-layout">
              <div ref={ref}>
                <CustomReportTemplateLayout
                  {...{
                    gridData,
                    setGridData,
                    layouts,
                    setLayouts,
                    mode: isEditable ? "create" : "view",
                    setComponentModal,
                    setEditItem,
                    selectedTiles,
                    handleSelect,
                  }}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="m-auto py-5">
            {/* <Empty description="No Data. Add Some " /> */}
            <Empty />
          </div>
        )}

        {/* buttons */}
        {!["home", "view"].includes(mode) && (
          <div className="d-flex align-items-center justify-content-end gap-2 pt-2">
            <Button type="primary" onClick={handleSubmit} disabled={!gridData?.length}>
              {cp === "true" ? "Create" : mode === "edit" ? "Update" : "Create"}
            </Button>
            <Button
              type="primary"
              className="bg-secondary"
              onClick={() => navigate("/report-template-list")}
              htmlType="button"
            >
              Cancel
            </Button>
          </div>
        )}
      </div>

      {updateTemplateModal && (
        <MetaCustomReportTemplateModal
          {...{
            show: updateTemplateModal,
            setShow: setUpdateTemplateModal,
            mode: cp === "true" ? "copy" : "edit",
            data,
            setData,
            setGridData,
            setLayouts,
            getData: getTemplate,
          }}
        />
      )}
      {componentModal && (
        <CustomReportTemplateComponentModal
          {...{
            show: componentModal,
            setShow: setComponentModal,
            mode: "create",
            editItem,
            setEditItem,
            setAddItemModal,
            setGridData,
            gridData,
            data,
          }}
        />
      )}
      {addItemModal && (
        <CustomReportTemplateAddItemModal
          {...{
            show: addItemModal,
            setShow: setAddItemModal,
            mode: gridData.find((item) => item._id === editItem._id) ? "edit" : "create",
            editItem,
            setEditItem,
            data,
            gridData,
            setGridData,
            setData,
            ref,
            setComponentModal,
          }}
        />
      )}
    </Spin>
  );
};

export default CustomReportTemplateBody;

CustomReportTemplateBody.propTypes = {
  id: PropTypes.string.isRequired,
  mode: PropTypes.string.isRequired,
  layouts: PropTypes.object,
  setLayouts: PropTypes.func,
  gridData: PropTypes.array,
  setGridData: PropTypes.func,
  selectedTiles: PropTypes.array,
  handleSelect: PropTypes.func,
};
